














import { defineComponent, onBeforeUnmount, onMounted, ref } from '@vue/composition-api';
import OlbMapInfoWindow from './mapInfoWindow.vue';
import OlbButton from '../button/button.vue';
import { mapStyles } from './mapConfig';
import Vue, { VueConstructor } from 'vue';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
const { useActions } = createNamespacedHelpers('googleStore');
import PortalVue from 'portal-vue';
Vue.use(PortalVue);

// currently, no types for the MarkerClusterer API is available
// eslint-disable-next-line @typescript-eslint/no-explicit-any
/* global google */
declare let MarkerClusterer: any;

interface Marker {
	Geolocation: {
		Lat: number;
		Lng: number;
	};
	Type: string;
	Name: string;
	Address: {
		Street: string;
		Zip: number;
		City: string;
	};
	Contact: {
		Url: {
			Value: string;
		};
	};
}

export default defineComponent({
	components: {
		OlbMapInfoWindow,
		OlbButton,
	},
	props: {
		jsonurl: { type: String },
		center: { type: String, default: '51, 10' },
		zoom: { type: String },
		mapclass: { type: String },
		location: { type: String },
		googleMapsApiKey: {
			type: String,
		},
		googleMapsClientId: {
			type: String,
		},
	},
	setup(props) {
		const { loadGoogleMapsAPI } = useActions(['loadGoogleMapsAPI']);
		const hasConsent = ref(false);
		const mapDiv = ref();
		const mapLoaded = ref(true);

		function isGoogleMapsConsentGiven() {
			if (typeof (window as any).IsGoogleMapsConsentAvailable === 'function') {
				return (window as any).IsGoogleMapsConsentAvailable();
			}
			return false;
		}

		function openUserCentrics() {
			if (typeof (window as any).ShowGoogleMapsConsent === 'function') {
				return (window as any).ShowGoogleMapsConsent();
			}
		}

		function createInfoWindow(marker: Marker) {
			const InfoWindow: VueConstructor = Vue.extend(OlbMapInfoWindow);
			const infoWindowInstance = new InfoWindow({
				propsData: {
					name: marker.Name,
					street: marker.Address.Street,
					zip: marker.Address.Zip,
					city: marker.Address.City,
					url: marker.Contact.Url.Value,
				},
			});
			infoWindowInstance.$mount();
			return new google.maps.InfoWindow({
				content: infoWindowInstance.$el,
			});
		}
		function createMarker(lat: number, lng: number, map: google.maps.Map, options: google.maps.MapOptions) {
			return new google.maps.Marker({
				position: new google.maps.LatLng(lat, lng),
				map: map,
				...options,
			});
		}
		async function fetchMarkers(url: string) {
			const markers = await fetch(url).then((data) => data.json());
			return markers.Marker;
		}
		function attachMarkerToInfoWindow(
			marker: google.maps.Marker,
			map: google.maps.Map,
			infoWindow: google.maps.InfoWindow,
			openInfoWindow: google.maps.InfoWindow,
		) {
			if (openInfoWindow) {
				openInfoWindow.close();
			}
			infoWindow.open({
				anchor: marker,
				map,
			});
			return infoWindow;
		}
		async function createMap() {
			const map = new google.maps.Map(mapDiv.value as HTMLElement, {
				zoom: props.zoom ? parseInt(props.zoom) : 8,
				minZoom: 5,
				maxZoom: 17,
				center: {
					lat: props.center ? parseFloat(props.center.split(',')[0]) : 0,
					lng: props.center ? parseFloat(props.center.split(',')[1]) : 0,
				},
				disableDefaultUI: true,
				zoomControl: true,
				zoomControlOptions: {
					position: google.maps.ControlPosition.RIGHT_BOTTOM,
				},
				...mapStyles,
			});
			return map;
		}

		async function renderMap() {
			const map = await createMap();
			if (props.location) {
				const geocoder = new google.maps.Geocoder();
				geocoder.geocode(
					{
						address: `${props.location}, Deutschland`,
					},
					(results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
						if (status === google.maps.GeocoderStatus.OK && results) {
							map.setCenter(results[0].geometry.location);
							results[0].geometry.bounds && map.panToBounds(results[0].geometry.bounds);
							const options: google.maps.MarkerOptions = {
								icon: {
									url: `https://www.olb.de/assets/img/svg/marker-olb.svg`,
									size: new google.maps.Size(52, 70),
									origin: new google.maps.Point(0, 0),
									anchor: new google.maps.Point(18, 50),
								},
							};
							createMarker(
								results[0].geometry.location.lat(),
								results[0].geometry.location.lng(),
								map,
								options,
							);
						} else {
							console.log(`Geocoder failed due to: ${status}`);
						}
						mapLoaded.value = true;
					},
				);
			}
			if (props.jsonurl) {
				const markers = await fetchMarkers(props.jsonurl as string);
				let openInfoWindow: google.maps.InfoWindow;
				const googleMapsMarkers: google.maps.Marker[] = [];
				markers.forEach((marker: Marker) => {
					const options: google.maps.MarkerOptions = {
						icon: {
							url: `https://www.olb.de/assets/img/svg/marker-${marker.Type}.svg`,
							size: new google.maps.Size(52, 70),
							origin: new google.maps.Point(0, 0),
							anchor: new google.maps.Point(18, 50),
						},
					};
					const newMarker = createMarker(
						marker['Geolocation']['Lat'],
						marker['Geolocation']['Lng'],
						map,
						options,
					);
					const infoWindow = createInfoWindow(marker);
					newMarker.addListener('click', () => {
						openInfoWindow = attachMarkerToInfoWindow(newMarker, map, infoWindow, openInfoWindow);
					});
					googleMapsMarkers.push(newMarker);
				});
				new MarkerClusterer(map, googleMapsMarkers, {
					averageCenter: true,
					maxZoom: 14,
					minimumClusterSize: 3,
					cssClass: 'absolute rounded bg-primary font-display text-white',
					styles: [
						{
							width: 25,
							height: 25,
							textSize: 15,
						},
						{
							width: 40,
							height: 40,
							textSize: 18,
						},
						{
							width: 50,
							height: 50,
							textSize: 18,
						},
					],
					gridSize: 80,
					zoomOnClick: false,
				});
				mapLoaded.value = true;
			}
		}

		const consentListener = async () => {
			if (isGoogleMapsConsentGiven()) {
				mapLoaded.value = false;
				hasConsent.value = true;
				window.addEventListener('googleMapsAPILoaded', () => {
					if (typeof google === 'object') {
						renderMap();
					}
				});
				loadGoogleMapsAPI({ apiKey: props.googleMapsApiKey, clientId: props.googleMapsClientId });
			}
		};

		onMounted(() => {
			window.addEventListener('GoogleMapsConsentAvailable', consentListener);
		});

		onBeforeUnmount(() => {
			window.removeEventListener('GoogleMapsConsentAvailable', consentListener);
		});
		return { hasConsent, openUserCentrics, mapDiv, mapLoaded };
	},
});
